<template>
  <v-dialog
    :model-value="true"
    :retain-focus="false"
    width="90vw"
    max-width="1200"
    content-class="ra-dialog-overlay"
    persistent
  >
    <section
      class="ra-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="リスクアセスメント入力"
    >
      <div class="ra-dialog__body">
        <header class="ra-dialog__header">
          <h2 class="ra-dialog__title">
            リスクアセスメント入力
          </h2>
        </header>

        <RiskAssessmentVersionSelector
          :versions="form.versions"
          :model-value="form.selectedVersionType"
          :skip-label="form.skipVersionLabel"
          :disabled="form.isSubmitting"
          @update:model-value="form.selectVersion"
          @report-requested="emit('report-requested')"
        />

        <v-alert
          v-if="form.errorMessage"
          class="ra-dialog__error"
          type="error"
          variant="tonal"
          density="compact"
          :closable="form.recoveryCode === null"
          @click:close="form.clearError"
        >
          {{ form.errorMessage }}
        </v-alert>

        <div class="ra-dialog__questions">
          <!-- 「どちらにも該当しない」は回答すべき設問が無いため、表を出さず完了だけをできるようにする。 -->
          <p
            v-if="form.isSkipped"
            class="ra-dialog__skip-notice"
          >
            <br>
            このまま「完了」すると、リスクアセスメントをスキップしてハイリスク疑いの報告に進めます。
          </p>
          <RiskAssessmentQuestionTable
            v-else-if="form.version"
            :version="form.version"
            :draft-map="form.draftMap"
            :previous-answer-map="form.previousAnswerMap"
            :previous-completed-at="form.previousAssessment?.completedAt ?? null"
            :disabled="form.isSubmitting"
            @select-answer-type="form.selectAnswerType"
            @toggle-detail="form.toggleDetail"
          />
          <p
            v-else
            class="ra-dialog__empty"
          >
            入力シートを選択してください。
          </p>
        </div>
      </div>

      <RiskAssessmentScoreSummary
        v-if="!form.isSkipped"
        :summary="form.summary"
      />

      <p
        v-if="!form.isSkipped"
        class="ra-dialog__hold-notice"
      >
        {{ holdNotice }}
      </p>

      <footer class="ra-dialog__actions">
        <v-btn
          variant="flat"
          color="#9e9e9e"
          min-width="112"
          :disabled="form.isSubmitting"
          @click="emit('close')"
        >
          閉じる
        </v-btn>
        <v-tooltip
          :disabled="form.completable || form.isSubmitting || form.recoveryCode === 'edit_period_expired'"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps">
              <v-btn
                color="#c8384f"
                variant="flat"
                min-width="112"
                :disabled="!form.completable || form.recoveryCode === 'edit_period_expired'"
                :loading="form.isSubmitting"
                @click="handleComplete"
              >
                完了
              </v-btn>
            </span>
          </template>
          <span>{{ incompleteReason }}</span>
        </v-tooltip>
      </footer>
    </section>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { RiskAssessmentResult } from "@/vue/entities/risk-assessment";
import type { RiskAssessmentForm } from "../model/useRiskAssessmentForm";
import RiskAssessmentQuestionTable from "./RiskAssessmentQuestionTable.vue";
import RiskAssessmentScoreSummary from "./RiskAssessmentScoreSummary.vue";
import RiskAssessmentVersionSelector from "./RiskAssessmentVersionSelector.vue";

const props = defineProps<{
  form: RiskAssessmentForm;
  // 相談ごとに保持する区分の表示名。未指定時は同一カルテ滞在中のみ保持する。
  holdScopeLabel?: string | null;
}>();

const emit = defineEmits<{
  close: [];
  completed: [RiskAssessmentResult];
  "report-requested": [];
}>();

// 修正可能期限はボトムバー側に表示するため、ダイアログでは扱わない。

const holdNotice = computed<string>(() =>
  props.holdScopeLabel
    ? `※入力途中で閉じても、${props.holdScopeLabel}の画面内にいる間は相談ごとに内容が保持されます。`
    : "※入力途中で閉じても、このカルテを開いている間は内容が保持されます。"
);

// 完了ボタンが非活性な理由をツールチップで示す（ui-design.md 4.6）。
// 未回答が残っていると完了できないため（FR-007 / AC-002）、残り問数まで出して迷わせない。
const incompleteReason = computed<string>(() => {
  if (!props.form.version) return "入力シートを選択してください";

  return `未回答の設問が ${props.form.summary.unansweredCount} 問あります`;
});

const handleComplete = async (): Promise<void> => {
  const result = await props.form.submit();
  if (result) emit("completed", result);
};
</script>

<style lang="scss">
// v-dialog の中身は body 直下へ teleport されるため scoped が効かない。
// 画面の高さをできるだけ使い切るため、ダイアログ全体の上下左右余白を16pxにする。
.v-overlay__content.ra-dialog-overlay {
  margin: 16px;
  max-height: calc(100% - 32px);
}
</style>

<style lang="scss" scoped>
.ra-dialog {
  background-color: #fff;
  border-radius: 4px;
  // オーバーレイ側で画面高 - 32px に制限しているため、ここではその高さを使い切る。
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 8px;
}

.ra-dialog__header {
  background-color: #fdeff2;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
}

.ra-dialog__title {
  color: #c8384f;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}


// ダイアログ内で唯一のスクロール領域。Ver選択ブロックも設問表と一緒に流れる
// （常時表示する必要がなく、そのぶん設問の表示高さを稼ぐため）。
// 集計行・注意書き・フッターだけがこの下に固定で残る。
.ra-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.ra-dialog__error {
  margin-top: 12px;
}

.ra-dialog__questions {
  margin-top: 16px;
}

.ra-dialog__empty {
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
  padding: 32px 0;
}

.ra-dialog__skip-notice {
  text-align: center;
  color: rgba(0, 0, 0, 0.7);
  font-size: 14px;
  line-height: 1.8;
  padding: 32px 0;
}

.ra-dialog__hold-notice {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
  margin: 4px 0 0;
  text-align: center;
}

.ra-dialog__actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 8px;
}
</style>
